import { Card, List, MenuItem, Popper } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { MutableRefObject, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { siteMeilisearchLockspaceSearchKeyKeys } from 'src/api/query-keys/site/meilisearch-lockspace-search-key';
import { siteMeilisearchSearchKeyKeys } from 'src/api/query-keys/site/meilisearch-search-key';
import { useApplications } from 'src/features/applications/site/hooks/use-applications';
import { useAvailableSites } from 'src/hooks/api/use-available-sites';
import { useUserStore } from 'src/store/user-store';
import { SiteType } from 'src/types/enum/site-type';
import { UserRole } from 'src/types/enum/user-role';

interface Props {
  anchorRef: MutableRefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  onMouseLeave?: () => void;
  onMouseEnter?: () => void;
  placement: 'bottom-end' | 'right-start';
}

export const AvailableSitesPopper = ({
  anchorRef,
  open,
  onClose,
  onMouseLeave,
  onMouseEnter,
  placement,
}: Props) => {
  const {
    setViewContext,
    directoryToken,
    isChangingDirectoryToken,
    setIsChangingDirectory,
    setDirectoryToken,
    setDirectoryTokenLockspace,
    setAdminToken,
    setRole,
    setIsSSO,
  } = useUserStore();

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const { data: availableSites } = useAvailableSites();

  const { data: apps } = useApplications(!!directoryToken);

  const handleChangeContext = async (
    directoryToken: string,
    adminToken: string,
    role: UserRole,
  ) => {
    setViewContext('site');
    setIsChangingDirectory(true);
    setDirectoryToken(directoryToken);
    setAdminToken(adminToken);
    setRole(role);
    setIsSSO(false);

    queryClient.invalidateQueries({
      queryKey: [siteMeilisearchSearchKeyKeys.details()],
    });
  };

  useEffect(() => {
    if (apps && isChangingDirectoryToken && directoryToken) {
      const directoryTokenLockspace =
        apps.find(app => app.name === 'Lockspace')?.resolvedParams?.[0]
          ?.metadata?.tenantToken || '';

      setDirectoryTokenLockspace(directoryTokenLockspace);

      queryClient
        .invalidateQueries({
          queryKey: [siteMeilisearchLockspaceSearchKeyKeys.details()],
        })
        .then(() => setIsChangingDirectory(false));

      navigate('/');

      onClose();
    }
  }, [isChangingDirectoryToken, apps, directoryToken]);

  return (
    <Popper
      anchorEl={anchorRef.current}
      open={open}
      placement={placement}
      onMouseLeave={onMouseLeave}
      onMouseEnter={onMouseEnter}
      sx={{
        margin: placement === 'bottom-end' ? '10px 0 !important' : undefined,
        top: placement === 'right-start' ? '-10px !important' : undefined,
      }}>
      <Card variant="outlined" sx={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <List>
          {availableSites?.resources
            ?.filter(site => site.type === SiteType.RESIDENTIAL)
            .map(site => (
              <MenuItem
                key={site.token}
                onClick={() => {
                  handleChangeContext(
                    site.token,
                    site.siteAdminToken,
                    site.role,
                  );
                }}>
                {site.name}
              </MenuItem>
            ))}
        </List>
      </Card>
    </Popper>
  );
};
